import React, { useEffect, useState } from 'react';
import { useCourseModuleList } from '@/hooks/queries/course-module/useList';
import { useCourseModuleCreate, useCourseModuleUpdate, useCourseModuleDelete } from '@/hooks/mutations/course-module';
import { Module, ModuleCreateInput } from '@/types/modules';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Layers, ChevronRight, Plus, Edit2, Trash2, Lock, Unlock } from 'lucide-react';
import { cn } from '@/utils/styleUtils';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/Pagination';

interface ModuleListProps {
  courseId: string;
  selectedModuleId: string;
  onSelectModule: (id: string) => void;
}

export const ModuleList = ({ courseId, selectedModuleId, onSelectModule }: ModuleListProps) => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);

  useEffect(() => {
    setPage(1);
  }, [courseId]);

  const [formData, setFormData] = useState<ModuleCreateInput>({
    courseId: courseId,
    isLocked: false,
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: modulesData, isLoading, refetch } = useCourseModuleList({
    courseId,
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createModule, isPending: isCreating } = useCourseModuleCreate();
  const { mutate: updateModule, isPending: isUpdating } = useCourseModuleUpdate();
  const { mutate: deleteModule } = useCourseModuleDelete();

  const handleOpenCreate = () => {
    setSelectedModule(null);
    setFormData({
      courseId,
      isLocked: false,
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (e: React.MouseEvent, mod: Module) => {
    e.stopPropagation();
    setSelectedModule(mod);
    setFormData({
      courseId,
      isLocked: mod.isLocked,
      translations: [
        { locale: 'uz', title: mod.title || '', description: mod.description || '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, mod: Module) => {
    e.stopPropagation();
    setSelectedModule(mod);
    setIsDeleteOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedModule) {
      updateModule(
        { id: selectedModule.id, data: { isLocked: formData.isLocked, translations: formData.translations } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Modul yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Modul yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createModule(
        { ...formData, courseId },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Yangi modul yaratildi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Modul yaratilmadi', variant: 'destructive' }),
        }
      );
    }
  };

  const handleDelete = () => {
    if (!selectedModule) return;
    deleteModule(selectedModule.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Modul o'chirildi" });
        setIsDeleteOpen(false);
        if (selectedModuleId === selectedModule.id) onSelectModule('');
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Modul o'chirilmadi", variant: 'destructive' }),
    });
  };

  const modulesList: Module[] = modulesData?.data?.data || modulesData?.data || [];

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="p-4 border-b border-border bg-slate-50/50 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Layers className="size-4 text-primary" />
            Modullar
          </h2>
          <p className="text-xs text-slate-500 mt-1">{modulesList.length} ta modul</p>
        </div>
        <Button size="sm" onClick={handleOpenCreate} className="h-8 gap-1 px-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border-0">
          <Plus className="size-3.5" /> <span className="hidden xl:inline">Yangi</span>
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
        {isLoading ? (
          <div className="py-10"><Loader /></div>
        ) : modulesList.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">Modullar topilmadi</div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
            {modulesList.map((mod) => {
              const isSelected = selectedModuleId === mod.id;
              const title = mod.title || 'Nomsiz modul';
              return (
                <div
                  key={mod.id}
                  onClick={() => onSelectModule(mod.id)}
                  className={cn(
                    'group cursor-pointer w-full flex flex-col p-3 rounded-xl border transition-all duration-200',
                    isSelected 
                      ? 'bg-blue-50/50 border-primary shadow-sm' 
                      : 'border-transparent hover:bg-slate-50 hover:border-slate-200 bg-white'
                  )}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className={cn(
                        "flex items-center justify-center shrink-0 size-5 text-[10px] font-bold rounded-md",
                        isSelected ? "bg-primary text-white" : "bg-slate-100 text-slate-600"
                      )}>
                        {mod.orderIndex}
                      </span>
                      <span className={cn("text-sm font-medium truncate", isSelected ? 'text-primary' : 'text-slate-700')}>
                        {title}
                      </span>
                    </div>
                    <ChevronRight className={cn("size-4 shrink-0 transition-transform", isSelected ? 'text-primary' : 'text-slate-300 group-hover:text-slate-400')} />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500 pl-7">
                    <div className="flex items-center gap-2">
                      <span>{mod.lessonCount || 0} dars</span>
                      <span>•</span>
                      <span>{mod.testCount || 0} test</span>
                    </div>
                    <div className={cn("flex items-center gap-1 transition-opacity", isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100")}>
                      <button onClick={(e) => handleOpenEdit(e, mod)} className="p-1 text-slate-400 hover:text-primary transition-colors">
                        <Edit2 className="size-3.5" />
                      </button>
                       <button onClick={(e) => handleDeleteClick(e, mod)} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                         <Trash2 className="size-3.5" />
                       </button>
                     </div>
                   </div>
                </div>
              );
            })}
            </div>
            <Pagination className="mt-3" currentPage={page} setCurrentPage={setPage} paginationInfo={modulesData?.meta?.pagination ?? modulesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedModule ? 'Modulni tahrirlash' : 'Yangi modul yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Qulflangan holat (isLocked)</Label>
                <p className="text-xs text-slate-500">Talaba oldingi modulni tugatmaguncha yopiq bo'ladi</p>
              </div>
              <Switch
                checked={formData.isLocked}
                onCheckedChange={(checked) => setFormData({ ...formData, isLocked: checked })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Modul nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="JavaScript Asoslari"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="O'zgaruvchilar, funksiyalar va tsikllar..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Modulni o'chirish"
        alertDescription="Haqiqatan ham ushbu modulni o'chirmoqchimisiz? Ichidagi darslar va testlar ham zarar ko'rishi mumkin."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};
